import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { CalendarDays, Check, ChevronDown, Clock, MapPin, Plane, Search, TrainFront } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const useIsMobile = () => {
  const [m, setM] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setM(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return m;
};

const LocationList = ({ locations, value, onPick }) => {
  const [query, setQuery] = useState("");
  const filtered = useMemo(
    () =>
      locations.filter(
        (l) => l.name.toLowerCase().includes(query.toLowerCase()) || l.city.toLowerCase().includes(query.toLowerCase())
      ),
    [locations, query]
  );
  return (
    <div className="w-full">
      <div className="p-3 border-b border-gray-100">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca stazione o aeroporto…"
          className="w-full h-10 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-fr-navy focus:bg-white transition-colors"
          data-testid="location-search-input"
        />
      </div>
      <ul className="max-h-72 overflow-y-auto py-1" data-testid="location-list">
        {filtered.map((l) => (
          <li key={l.code}>
            <button
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left"
              onClick={() => onPick(l)}
              data-testid={`location-option-${l.code}`}
            >
              {l.type === "airport" ? <Plane size={15} className="text-gray-400" /> : <TrainFront size={15} className="text-gray-400" />}
              <span className="flex-1">
                <span className="block font-semibold text-gray-900">{l.name}</span>
                <span className="block text-[11px] text-gray-400">{l.city}</span>
              </span>
              {l.delivery_min != null && (
                <span className="shrink-0 rounded-md bg-gray-50 border border-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-500">
                  consegna {l.delivery_min}–{l.delivery_max} min
                </span>
              )}
              {value?.code === l.code && <Check size={15} className="text-fr-red" />}
            </button>
          </li>
        ))}
        {filtered.length === 0 && <li className="px-4 py-6 text-sm text-gray-400 text-center">Nessuna stazione trovata</li>}
      </ul>
    </div>
  );
};

const TimeSelect = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-2">
    <Clock size={14} className="text-gray-400" />
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[88px] text-xs rounded-lg" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-64">
        {Array.from({ length: 48 }, (_, i) => {
          const h = String(Math.floor(i / 2)).padStart(2, "0");
          const m = i % 2 === 0 ? "00" : "30";
          return (
            <SelectItem key={`${h}:${m}`} value={`${h}:${m}`}>
              {h}:{m}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  </div>
);

const DateTimePanel = ({ label, date, time, onDate, onTime, isMobile }) => {
  const [month, setMonth] = useState(date);
  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">{label}</p>
        <TimeSelect value={time} onChange={onTime} testid={`${label.toLowerCase()}-time-select`} />
      </div>
      <Calendar
        mode="single"
        selected={date}
        onSelect={onDate}
        numberOfMonths={isMobile ? 1 : 2}
        month={month}
        onMonthChange={setMonth}
        locale={it}
        disabled={[{ before: new Date() }]}
        classNames={{
          months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
          caption: "flex justify-center pt-1 relative items-center",
          head_cell: "text-gray-400 font-medium text-[11px] w-9",
          cell: "h-9 w-9 text-center text-sm p-0 relative",
          day: "h-9 w-9 rounded-lg font-normal hover:bg-fr-navy hover:text-white transition-colors",
          day_selected: "bg-fr-navy text-white hover:bg-fr-navy",
          day_today: "border border-fr-red text-fr-red",
        }}
        data-testid={`${label.toLowerCase()}-calendar`}
      />
    </div>
  );
};

const FieldShell = React.forwardRef(({ step, label, children, testid, ...props }, ref) => (
  <button
    ref={ref}
    {...props}
    className="flex-1 min-w-0 w-full text-left rounded-xl border border-gray-200 bg-white px-4 py-2.5 hover:border-fr-navy/50 hover:shadow-card transition-all"
    data-testid={testid}
  >
    <span className="flex items-center gap-1.5 text-[10px] font-bold tracking-wider text-gray-400 uppercase">
      <span className="text-fr-navy">{step}</span> {label}
    </span>
    {children}
  </button>
));
FieldShell.displayName = "FieldShell";

export const SearchBar = ({ search, days, locations, onChange, onSearch }) => {
  const isMobile = useIsMobile();
  const [openPicker, setOpenPicker] = useState(null);

  const dateLabel = (d) => format(d, "d MMM", { locale: it });

  const Picker = ({ which, step, label, date, time }) => {
    const isDate = which === "pickup" || which === "dropoff";
    const content =
      which === "location" ? (
        <LocationList
          locations={locations}
          value={search.location}
          onPick={(l) => {
            onChange({ location: l });
            setOpenPicker(null);
          }}
        />
      ) : (
        <DateTimePanel
          label={label}
          isMobile={isMobile}
          date={search[which]}
          time={format(search[which], "HH:mm")}
          onDate={(d) => d && onChange({ [which]: new Date(`${format(d, "yyyy-MM-dd")}T${format(search[which], "HH:mm:ss")}`) })}
          onTime={(t) => {
            const [h, m] = t.split(":");
            const nd = new Date(search[which]);
            nd.setHours(Number(h), Number(m), 0, 0);
            onChange({ [which]: nd });
          }}
        />
      );

    const trigger = (
      <FieldShell step={step} label={isDate ? label : label} testid={`search-field-${which}`}>
        <span className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-900 truncate">
            {which === "location" ? (
              <>
                <MapPin size={14} className="text-fr-red shrink-0" /> {search.location.name}
              </>
            ) : (
              <>
                <CalendarDays size={14} className="text-fr-navy shrink-0" />
                {dateLabel(search[which])} <span className="text-gray-500 font-medium">{format(search[which], "HH:mm")}</span>
              </>
            )}
          </span>
          <ChevronDown size={14} className="text-gray-300 shrink-0" />
        </span>
      </FieldShell>
    );

    if (isMobile) {
      return (
        <Drawer open={openPicker === which} onOpenChange={(o) => setOpenPicker(o ? which : null)}>
          <DrawerTrigger asChild>{trigger}</DrawerTrigger>
          <DrawerContent className="rounded-t-2xl" data-testid={`picker-drawer-${which}`}>
            <DrawerTitle className="sr-only">{label}</DrawerTitle>
            {content}
          </DrawerContent>
        </Drawer>
      );
    }
    return (
      <Popover open={openPicker === which} onOpenChange={(o) => setOpenPicker(o ? which : null)}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent side="bottom" align="start" className="w-auto p-0 rounded-2xl overflow-hidden shadow-search" data-testid={`picker-popover-${which}`}>
          {content}
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <div className="sticky top-14 z-40 px-3 md:px-6 -mt-4 md:-mt-10 pb-2" data-testid="search-bar-wrapper">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-search border border-gray-100 p-3 md:p-3.5" data-testid="search-bar">
        <div className="flex flex-col md:flex-row gap-2 md:items-stretch">
          <div className="flex-1 min-w-0">
            <Picker which="location" step={1} label="Dove vuoi noleggiare?" />
          </div>
          <div className="flex flex-col md:flex-row gap-2 md:w-[340px]">
            <Picker which="pickup" step={2} label="Ritiro" />
            <Picker which="dropoff" step={3} label="Consegna" />
          </div>
          <button
            onClick={onSearch}
            className="h-[52px] md:h-auto rounded-xl bg-fr-navy hover:bg-fr-navydark active:scale-[0.98] text-white font-bold text-sm px-6 flex items-center justify-center gap-2 transition-all"
            data-testid="search-submit-button"
          >
            <Search size={16} /> Cerca • {days} giorni
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
